import { useQuery } from '@tanstack/react-query'
import { apiFetch } from './core'

/**
 * Who is signed in, what they may do, and who they work with.
 *
 * Split out of api/leads.ts, where it accounted for 27 of that module's 43
 * imports: useMe eight times, hasPerm six, useColleagues five, canWorkQueue
 * and Me four each. Everything that needed to ask "may this person do X" was
 * pulling in the whole lead CRUD surface to find out -- the same shape as
 * trackEvent living in api/admin.
 */

export interface Me {
  email: string
  name: string
  countryScope: string | null
  isAdmin: boolean
  role: string | null
  permissions: string[]
  // Whether this person works the lead queue. NOT a permission: Member is
  // auto-granted on first login and carries log_calls, so permission checks alone
  // treat every colleague who opens the CRM as a seller.
  takesLeads: boolean
}

// The single test for "may this person be given leads". Both halves matter — the
// permission is capability, takesLeads is role in the team.
export function canWorkQueue(me: Me | undefined): boolean {
  return !!me?.takesLeads && hasPerm(me, 'log_calls')
}

export function hasPerm(me: Me | undefined, perm: string): boolean {
  return !!me?.permissions?.includes(perm)
}

// Hantera: every tab with manage_leads, the Dubbletter tab alone with manage_users.
export function canOpenManage(me: Me | undefined): boolean {
  return hasPerm(me, 'manage_leads') || hasPerm(me, 'manage_users')
}

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<Me>('/api/me'),
    staleTime: 5 * 60_000,
    retry: false, // a 401 means "sign in" — surface it to AuthGate immediately
  })
}

// Active colleagues, for the Seller picker. `seller` is the closer a lead was
// handed to, and it used to be a free-text box — which is how one person ended
// up stored four different ways. Cached hard: the roster changes rarely and this
// renders on every table row.
export interface Colleague {
  email: string
  name: string
  /** Works the lead queue. Only these can be handed a lead — see useTransferLead. */
  takesLeads: boolean
}

export function useColleagues() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => apiFetch<{ users: Colleague[]; total: number }>('/api/users'),
    staleTime: 10 * 60_000,
  })
}

export interface OpenedLead {
  id: string
  company: string
  location: string
  stage: string
  openedAt: string | null
}

// Companies the current user opened today (RADAR-11 trace-back list).
// `enabled` gates the fetch so it only runs when the menu is actually opened.
export function useOpenedToday(enabled: boolean) {
  return useQuery({
    queryKey: ['opened-today'],
    queryFn: () => apiFetch<{ leads: OpenedLead[]; total: number }>('/api/leads/opened-today'),
    enabled,
    staleTime: 30_000,
  })
}
